
import { strFromU8, strToU8 } from "fflate";
import type { BrandIssue, BrandSpec } from "../../../types";
import { normalizeHex, colorDistance } from "../../../utils/colourUtils";
import {
  snapColours,
  mapSchemeColours,
  unzipPptxBase64,
  replaceSlideWithRewrittenCopy,
  type PptxZip,
} from "../../apply-brand/services/brandApplier";
import { writeNotesText } from "../../SpeakerNotes/services/speakerNotesOffice";
import { logError, logOfficeError, scrubMessage } from "../../../../../telemetry";

// ─── Constants ────────────────────────────────────────────────────────────────

const GROUP_TYPES = new Set(["group", "Group"]);
const MAX_GROUP_DEPTH = 5;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function errorMessage(e: unknown, fallback?: string): string {
  if (e instanceof Error) return e.message;
  return fallback ?? String(e);
}

/** connectorSystemName tag for all Deckorate brand-fix telemetry — mirrors brandApplier's convention. */
const BRAND_FIX_CONNECTOR = "deckorate";

/** Report a brand-fix failure through the shared telemetry pipeline (PII-scrubbed). */
function reportBrandFixError(reason: string, type: string, message: string): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: BRAND_FIX_CONNECTOR, reason },
  );
}

/** Read a zip entry's contents as a UTF-8 string, or null when the path is absent. */
function readZipText(files: PptxZip, path: string): string | null {
  const bytes = files[path];
  return bytes ? strFromU8(bytes) : null;
}

/** Brand colour palette as #RRGGBB strings. Exported so the results UI can preview the exact target colour a fix will apply. */
export function buildFixPalette(brand: BrandSpec): string[] {
  const { colors, fillColorMap, lineColor } = brand;
  return [
    colors.dk1, colors.dk2, colors.lt1, colors.lt2,
    colors.accent1, colors.accent2, colors.accent3,
    colors.accent4, colors.accent5, colors.accent6,
    fillColorMap.primary, fillColorMap.secondary, fillColorMap.tertiary,
    lineColor,
  ].flatMap((c) => {
    const n = normalizeHex(c);
    return n ? [`#${n}`] : [];
  });
}

/**
 * Nearest palette colour by plain RGB distance — never returns null for a
 * valid input. findNearestBrandColor excludes near-white/near-black inputs,
 * which left issues the checker flagged (e.g. #F4EAEC) permanently unfixable:
 * "Fix" ran, changed nothing, and the issue reappeared on every press.
 * Exported for the same before→after preview reason as buildFixPalette.
 */
export function nearestPaletteColor(hex: string, palette: string[]): string | null {
  const norm = normalizeHex(hex);
  if (!norm || palette.length === 0) return null;
  let best: string | null = null;
  let bestDist = Infinity;
  for (const p of palette) {
    const cand = normalizeHex(p);
    if (!cand) continue;
    const dist = colorDistance(norm, cand);
    if (dist < bestDist) { bestDist = dist; best = cand; }
  }
  return best ? `#${best}` : null;
}

/** Brand body font, resolved past any theme-reference placeholder. */
function resolveBodyFont(brand: BrandSpec): string {
  const preferred = brand.bodyTextFormat?.fontName || brand.fonts?.bodyFont || "";
  if (preferred && !preferred.startsWith("+")) return preferred;
  const fallback = brand.fonts?.bodyFont || "";
  return fallback.startsWith("+") ? "" : fallback;
}

function extractShapeName(block: string): string {
  return new RegExp(/<p:cNvPr\b[^>]*\bname="([^"]*)"/).exec(block)?.[1] ?? "";
}

// ─── Shape lookup (top-level + group children) ────────────────────────────────

/**
 * Find a shape by id (preferred) or name, searching group children too —
 * grouped shapes are NOT part of slide.shapes and were previously unfindable,
 * so their fixes always returned false.
 *
 * Name fallback matters after an OOXML fix reinserts a slide: shape ids are
 * regenerated but names survive.
 */
async function findShapeInSlide(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  shapeId: string | undefined,
  shapeName: string | undefined,
): Promise<PowerPoint.Shape | null> {
  const shapes = slide.shapes;
  shapes.load("items");
  await context.sync();
  for (const s of shapes.items) s.load("id,name,type");
  await context.sync();

  const direct =
    (shapeId && shapes.items.find((s) => s.id === shapeId)) ||
    (shapeName && shapes.items.find((s) => s.name === shapeName)) ||
    null;
  if (direct) return direct;

  for (const s of shapes.items) {
    if (!GROUP_TYPES.has(String(s.type ?? ""))) continue;
    try {
      const found = await findInGroup(context, s, shapeId, shapeName, 0);
      if (found) return found;
    } catch (err) {
      // Bounded by the group count on this slide (not per-shape scanning) —
      // worth tracing so a systematically-unreadable group is visible.
      logOfficeError(err, "brand-fix-find-in-group");
    }
  }
  return null;
}

async function findInGroup(
  context: PowerPoint.RequestContext,
  groupShape: PowerPoint.Shape,
  shapeId: string | undefined,
  shapeName: string | undefined,
  depth: number,
): Promise<PowerPoint.Shape | null> {
  if (depth >= MAX_GROUP_DEPTH) return null;

  groupShape.load("group");
  await context.sync();
  const children = groupShape.group.shapes;
  children.load("items");
  await context.sync();
  for (const c of children.items) c.load("id,name,type");
  await context.sync();

  const direct =
    (shapeId && children.items.find((s) => s.id === shapeId)) ||
    (shapeName && children.items.find((s) => s.name === shapeName)) ||
    null;
  if (direct) return direct;

  for (const c of children.items) {
    if (!GROUP_TYPES.has(String(c.type ?? ""))) continue;
    const nested = await findInGroup(context, c, shapeId, shapeName, depth + 1);
    if (nested) return nested;
  }
  return null;
}

// ─── Navigation ───────────────────────────────────────────────────────────────

/**
 * What navigateToIssue actually managed to do — lets the UI show an accurate
 * confirmation ("jumped to the shape" vs. just "jumped to the slide") instead
 * of optimistically claiming a shape was selected when it wasn't found.
 * - "shape": slide selected AND the offending shape found + selected.
 * - "slide": slide selected, but no shape reference to select (slide-level
 *   issues like animations/transitions/notes/comments) or the shape couldn't
 *   be found.
 * - "none": couldn't even select the slide (host limitation, or the slide
 *   index no longer exists).
 */
export type NavigateOutcome = "shape" | "slide" | "none";

/**
 * Navigate to the slide for the issue and, when possible, select the offending shape.
 * Both setSelectedSlides and setSelectedShapes require PowerPointApi 1.5 — wrapped in
 * try/catch so the function degrades gracefully in older hosts.
 */
export async function navigateToIssue(issue: BrandIssue): Promise<NavigateOutcome> {
  if (!hasPpt()) return "none";

  let outcome: NavigateOutcome = "none";

  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();

    const slide = slides.items[issue.slideIndex];
    if (!slide) return;

    // Navigate to slide.
    try {
      context.presentation.setSelectedSlides([slide.id]);
      await context.sync();
      outcome = "slide";
    } catch (err) {
      logOfficeError(err, "brand-fix-set-selected-slides");
      return;
    }

    // Select the shape (works for top-level shapes; group children may refuse).
    if (issue.shapeId || issue.shapeName) {
      try {
        const found = await findShapeInSlide(context, slide, issue.shapeId, issue.shapeName);
        if (found) {
          slide.setSelectedShapes([found.id]);
          await context.sync();
          outcome = "shape";
        }
      } catch (err) {
        logOfficeError(err, "brand-fix-set-selected-shapes");
      }
    }
  });

  return outcome;
}

// ─── Single-issue fix ─────────────────────────────────────────────────────────

async function applyFillColorFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  issue: BrandIssue,
  palette: string[],
): Promise<boolean> {
  if (!issue.actual) return false;
  const nearest = nearestPaletteColor(issue.actual, palette);
  if (!nearest) return false;

  shape.fill.setSolidColor(nearest);
  await context.sync();
  // Verify the write stuck.
  shape.fill.load("foregroundColor");
  await context.sync();
  const got = normalizeHex(String(shape.fill.foregroundColor ?? "").replace("#", ""));
  return got === normalizeHex(nearest.replace("#", ""));
}

async function applyLineColorFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  brand: BrandSpec,
): Promise<boolean> {
  const lineHex = normalizeHex(brand.lineColor);
  if (!lineHex) return false;
  shape.lineFormat.color = `#${lineHex}`;
  await context.sync();
  return true;
}

async function applyFontColorFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  issue: BrandIssue,
  palette: string[],
): Promise<boolean> {
  if (!issue.actual?.startsWith("#")) return false;

  const nearest = nearestPaletteColor(issue.actual, palette);
  if (!nearest) return false;
  shape.textFrame.textRange.font.color = nearest;
  await context.sync();
  return true;
}

/** Italic-only — text colour is handled by the separate `fontColor` category/fix. */
async function applyFontStyleFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  issue: BrandIssue,
): Promise<boolean> {
  if (issue.actual !== "Italic") return false;
  shape.textFrame.textRange.font.italic = false;
  await context.sync();
  return true;
}

async function applyFontSizeFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  brand: BrandSpec,
): Promise<boolean> {
  const sz = brand.bodyTextFormat.fontSize;
  if (sz <= 0) return false;
  shape.textFrame.textRange.font.size = sz;
  await context.sync();
  return true;
}

/** Clamps the shape back onto the slide canvas. Requires PowerPointApi 1.10 (`PageSetup`) — unsupported hosts return false, leaving the issue unfixed rather than guessing. */
async function applyOffSlideFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
): Promise<boolean> {
  try {
    shape.load("left,top,width,height");
    const pageSetup = context.presentation.pageSetup;
    pageSetup.load("slideWidth,slideHeight");
    await context.sync();

    const { left, top, width, height } = shape;
    const { slideWidth, slideHeight } = pageSetup;
    if (slideWidth <= 0 || slideHeight <= 0) return false;

    const maxLeft = Math.max(0, slideWidth - width);
    const maxTop = Math.max(0, slideHeight - height);
    const newLeft = Math.min(Math.max(left, 0), maxLeft);
    const newTop = Math.min(Math.max(top, 0), maxTop);
    if (newLeft === left && newTop === top) return false;

    shape.left = newLeft;
    shape.top = newTop;
    await context.sync();
    return true;
  } catch (err) {
    logOfficeError(err, "brand-fix-off-slide");
    return false;
  }
}

/**
 * Reveals a hidden shape (visible = true) rather than deleting it — a shape
 * hidden on the slide may have been hidden intentionally, so surfacing it is
 * the non-destructive fix. Requires PowerPointApi 1.10 — unsupported hosts
 * return false.
 */
async function applyHiddenObjectFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
): Promise<boolean> {
  try {
    shape.visible = true;
    await context.sync();
    return true;
  } catch (err) {
    logOfficeError(err, "brand-fix-hidden-object");
    return false;
  }
}

const DOUBLE_WORD_FIX_RE = /\b(\w+)(\s+)\1\b/gi;
const MULTIPLE_SPACES_FIX_RE = / {2,}/g;

/** Collapses a repeated word ("the the" → "the") across the shape's whole text range. */
async function applyDoubleWordsFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
): Promise<boolean> {
  const textRange = shape.textFrame.textRange;
  textRange.load("text");
  await context.sync();
  const text = textRange.text ?? "";
  const fixed = text.replace(DOUBLE_WORD_FIX_RE, "$1");
  if (fixed === text) return false;
  textRange.text = fixed;
  await context.sync();
  return true;
}

/** Collapses runs of multiple spaces down to one across the shape's whole text range. */
async function applyMultipleSpacesFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
): Promise<boolean> {
  const textRange = shape.textFrame.textRange;
  textRange.load("text");
  await context.sync();
  const text = textRange.text ?? "";
  const fixed = text.replace(MULTIPLE_SPACES_FIX_RE, " ");
  if (fixed === text) return false;
  textRange.text = fixed;
  await context.sync();
  return true;
}

/**
 * Dispatch to the fix routine for this issue's category; unsupported
 * categories are a no-op. Deliberately has NO case for `emptyPlaceholder` or
 * `commentsPresent` — those are report-only (navigate-to-issue only), never
 * auto-fixed, because "fixing" them has no non-destructive form (deleting a
 * placeholder or a review comment IS the only possible fix), and
 * `emptyPlaceholder` in particular used to auto-delete via `shape.delete()`:
 * the detection relied on `placeholderFormat.containedType === null`, which
 * is also `null` for every ordinary text placeholder that HAS real typed
 * text (it only resolves to something for a nested table/chart/picture/
 * SmartArt) — so it deleted live title/body text shapes across a whole deck.
 * Never re-enable an auto-delete fix here without a detection signal proven
 * safe in real PowerPoint, not just in the mocked test suite.
 *
 * `hiddenObject` IS auto-fixable, but via reveal (`applyHiddenObjectFix`)
 * rather than delete — non-destructive. `animationsPresent`/
 * `transitionsPresent` are auto-fixable via the OOXML route
 * (`fixIssuesViaOoxml`'s "slide" target) since removing an effect deletes no
 * content. `notesPresent` never reaches this switch at all — it's always
 * `fixVia: "ooxml"` (target "notes") and is handled in `fixIssuesViaOoxml` /
 * `applyNotesFix`, which DOES clear the notes text — a deliberately
 * destructive fix per explicit product decision (unlike every other OOXML
 * target here).
 *
 * `fontType` also never reaches this switch — it's always `fixVia: "ooxml"`
 * (see brandChecker.ts's checkFontType/scanShapeOoxml) and goes through
 * `fixIssuesViaOoxml`'s `forceFontInRuns`, which rewrites every run's
 * `<a:latin>` explicitly. A prior version tried a direct
 * `shape.textFrame.textRange.font.name` write first: it verified as "stuck"
 * immediately (read-back matched), but on some shapes the write didn't
 * survive an actual save — the issue reappeared on the next scan. Routing
 * fontType through the OOXML rewrite unconditionally removes that ambiguity.
 */
async function applyShapeFix(
  context: PowerPoint.RequestContext,
  shape: PowerPoint.Shape,
  issue: BrandIssue,
  brand: BrandSpec,
  palette: string[],
): Promise<boolean> {
  switch (issue.category) {
    case "fillColor": return applyFillColorFix(context, shape, issue, palette);
    case "lineColor": return applyLineColorFix(context, shape, brand);
    case "fontColor": return applyFontColorFix(context, shape, issue, palette);
    case "hiddenObject": return applyHiddenObjectFix(context, shape);
    case "fontStyle": return applyFontStyleFix(context, shape, issue);
    case "fontSize": return applyFontSizeFix(context, shape, brand);
    case "offSlide": return applyOffSlideFix(context, shape);
    case "doubleWords": return applyDoubleWordsFix(context, shape);
    case "multipleSpaces": return applyMultipleSpacesFix(context, shape);
    default: return false;
  }
}

/**
 * Apply the brand fix for one issue. Returns true only when the fix was
 * written AND verified; false when the shape could not be found, the write
 * did not stick, or the fix is not supported for this category.
 */
export async function fixIssue(issue: BrandIssue, brand: BrandSpec): Promise<boolean> {
  if (!hasPpt()) return false;

  // Tables / SmartArt / charts — unreachable via the shape API.
  if (issue.fixVia === "ooxml") {
    try {
      return await fixIssuesViaOoxml([issue], brand);
    } catch (e) {
      const msg = errorMessage(e);
      reportBrandFixError("brand-fix-ooxml-direct", "BrandFixError", msg);
      return false;
    }
  }

  if (!issue.shapeId && !issue.shapeName) return false;

  let fixed = false;

  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();

    const slide = slides.items[issue.slideIndex];
    if (!slide) return;

    const shape = await findShapeInSlide(context, slide, issue.shapeId, issue.shapeName);
    if (!shape) return;

    const palette = buildFixPalette(brand);

    try {
      fixed = await applyShapeFix(context, shape, issue, brand, palette);
    } catch (e) {
      const msg = errorMessage(e);
      reportBrandFixError("brand-fix-issue", "BrandFixError", msg);
      fixed = false;
    }
  });

  return fixed;
}

// ─── Batch fix ────────────────────────────────────────────────────────────────

/**
 * Fix all issues in the supplied list. Returns the IDs of successfully fixed
 * issues.
 *
 * Office.js fixes run first; OOXML fixes run last, grouped per slide — an
 * OOXML fix reinserts its slide (regenerating shape ids), so running it
 * before shape-API fixes on the same slide would strand them.
 */
export async function fixAllIssues(
  issues: BrandIssue[],
  brand: BrandSpec,
): Promise<string[]> {
  const fixedIds: string[] = [];
  const officeJsIssues = issues.filter((i) => i.fixVia !== "ooxml");
  const ooxmlIssues = issues.filter((i) => i.fixVia === "ooxml");

  for (const issue of officeJsIssues) {
    if (await fixIssue(issue, brand)) fixedIds.push(issue.id);
  }

  const bySlide = new Map<number, BrandIssue[]>();
  for (const issue of ooxmlIssues) {
    const group = bySlide.get(issue.slideIndex) ?? [];
    group.push(issue);
    bySlide.set(issue.slideIndex, group);
  }
  for (const group of bySlide.values()) {
    try {
      if (await fixIssuesViaOoxml(group, brand)) {
        fixedIds.push(...group.map((i) => i.id));
      }
    } catch (e) {
      const msg = errorMessage(e);
      reportBrandFixError("brand-fix-ooxml-batch", "BrandFixError", msg);
    }
  }

  return fixedIds;
}

// ─── OOXML fix route ──────────────────────────────────────────────────────────

/**
 * Force an explicit font face onto every text run in the XML block:
 * existing <a:latin> faces are rewritten in place; run properties without one
 * get it appended (schema order: latin follows line/fill/effect properties);
 * runs with no <a:rPr> at all get one inserted.
 */
function forceFontInRuns(xml: string, font: string): string {
  const latin = `<a:latin typeface="${font}"/>`;
  let out = xml.replace(/(<a:latin\b[^>]*?)\btypeface="[^"]*"/g, `$1typeface="${font}"`);
  out = out.replace(
    /<a:(rPr|defRPr|endParaRPr)\b([^>]*?)\/>/g,
    (_m, tag: string, attrs: string) => `<a:${tag}${attrs}>${latin}</a:${tag}>`,
  );
  out = out.replace(
    /<a:(rPr|defRPr|endParaRPr)\b([^>]*)>([\s\S]*?)<\/a:\1>/g,
    (m, tag: string, attrs: string, inner: string) =>
      inner.includes("<a:latin") ? m : `<a:${tag}${attrs}>${inner}${latin}</a:${tag}>`,
  );
  out = out.replace(/<a:r>\s*<a:t>/g, `<a:r><a:rPr>${latin}</a:rPr><a:t>`);
  return out;
}

/** Snap explicit colours and convert theme colour refs to brand colours. */
function recolourBlock(xml: string, brand: BrandSpec): string {
  const { xml: snapped } = snapColours(xml, brand);
  const { xml: mapped } = mapSchemeColours(snapped, brand);
  return mapped;
}

/**
 * Recolour explicit <a:buClr> bullet colours to the nearest brand palette
 * colour. `snapColours` deliberately SKIPS <a:buClr> blocks (bullet colours
 * are treated as intentional when applying a whole brand template), so the
 * bulletColor fix needs its own pass rather than reusing `recolourBlock`.
 *
 * Only swaps the val="..." attribute (mirrors detection's own
 * BULLET_COLOR_RE), instead of requiring the whole <a:srgbClr> to be
 * self-closed — a bullet colour with a child element (e.g. <a:alpha>) has a
 * non-self-closed <a:srgbClr>...</a:srgbClr> and would otherwise never match,
 * silently leaving the issue detected but unfixable.
 */
function recolourBulletBlock(xml: string, palette: string[]): string {
  return xml.replace(
    /(<a:buClr>\s*<a:srgbClr\s+val=")([0-9A-F]{6})(")/gi,
    (match, pre: string, hex: string, post: string) => {
      const nearest = nearestPaletteColor(`#${hex}`, palette);
      if (!nearest) return match;
      const nearestHex = normalizeHex(nearest.replace("#", ""));
      return nearestHex ? `${pre}${nearestHex}${post}` : match;
    },
  );
}

/**
 * Apply `fn` to the block(s) matching the issue's shape name. When the name
 * no longer matches anything (shape renamed / slide reinserted) and
 * `requiredContent` is given, fall back to every block containing it.
 */
function transformNamedBlocks(
  xml: string,
  blockRe: RegExp,
  shapeName: string | undefined,
  requiredContent: string | null,
  fn: (block: string) => string,
): string {
  const nameLower = (shapeName ?? "").toLowerCase();
  let matchedByName = false;

  let out = xml.replace(blockRe, (block) => {
    if (requiredContent && !block.includes(requiredContent)) return block;
    if (nameLower && extractShapeName(block).toLowerCase() === nameLower) {
      matchedByName = true;
      return fn(block);
    }
    return block;
  });

  if (!matchedByName && requiredContent) {
    out = xml.replace(blockRe, (block) =>
      block.includes(requiredContent) ? fn(block) : block,
    );
  }
  return out;
}

/** Swap the slide's <p:bg> block(s) out for restorable placeholders, so downstream rewrites never touch the background. */
function preserveBackground(xml: string): { xml: string; bgBlocks: string[] } {
  const bgBlocks: string[] = [];
  const replaced = xml.replace(/<p:bg\b[^>]*\/>|<p:bg\b[\s\S]*?<\/p:bg>/g, (m) => {
    bgBlocks.push(m);
    return `<!--AIVA_BG_${bgBlocks.length - 1}-->`;
  });
  return { xml: replaced, bgBlocks };
}

function restoreBackground(xml: string, bgBlocks: string[]): string {
  if (bgBlocks.length === 0) return xml;
  return xml.replace(/<!--AIVA_BG_(\d+)-->/g, (_m, i: string) => bgBlocks[Number(i)] ?? "");
}

interface ShapeIssuesResult {
  xml: string;
  diagramFonts: boolean;
  diagramFills: boolean;
  chartFonts: boolean;
  clearNotes: boolean;
}

interface TargetFlags {
  diagramFonts: boolean;
  diagramFills: boolean;
  chartFonts: boolean;
  clearNotes: boolean;
}

/**
 * Diagram/chart/notes targets aren't reachable from slide XML — record which
 * kind of fix they need (for the caller's separate pass) instead of touching
 * any XML here. Returns whether the issue was a non-slide target.
 */
function flagNonSlideTarget(issue: BrandIssue, target: string, flags: TargetFlags): boolean {
  if (target === "diagram") {
    if (issue.category === "fontType") flags.diagramFonts = true;
    else flags.diagramFills = true;
    return true;
  }
  if (target === "chart") {
    if (issue.category === "fontType") flags.chartFonts = true;
    return true;
  }
  if (target === "notes") {
    flags.clearNotes = true;
    return true;
  }
  return false;
}

function applyShapeOrTableIssue(
  xml: string,
  issue: BrandIssue,
  target: string,
  brand: BrandSpec,
  bodyFont: string,
  palette: string[],
): string {
  const blockRe = target === "table"
    ? /<p:graphicFrame\b[\s\S]*?<\/p:graphicFrame>/g
    : /<p:sp\b[\s\S]*?<\/p:sp>/g;
  const requiredContent = target === "table" ? "<a:tbl" : null;

  return transformNamedBlocks(xml, blockRe, issue.shapeName, requiredContent, (block) => {
    if (issue.category === "fontType") return bodyFont ? forceFontInRuns(block, bodyFont) : block;
    if (issue.category === "bulletColor") return recolourBulletBlock(block, palette);
    return recolourBlock(block, brand);
  });
}

const TIMING_BLOCK_RE = /<p:timing>[\s\S]*?<\/p:timing>/;
const TRANSITION_BLOCK_RE = /<p:transition\b[^>]*\/>|<p:transition\b[^>]*>[\s\S]*?<\/p:transition>/;

/**
 * Slide-level fixes (animations/transitions) aren't matched by shape name —
 * `<p:timing>`/`<p:transition>` are direct children of `<p:sld>`, not scoped
 * to any one shape. Removing the whole block deletes the effect only, never
 * shape/text content.
 */
function applySlideLevelIssue(xml: string, issue: BrandIssue): string {
  if (issue.category === "animationsPresent") return xml.replace(TIMING_BLOCK_RE, "");
  if (issue.category === "transitionsPresent") return xml.replace(TRANSITION_BLOCK_RE, "");
  return xml;
}

/**
 * Apply every shape/table/slide-targeted issue directly to the slide XML.
 * Diagram- and chart-targeted issues aren't reachable from slide XML —
 * they're only flagged here, and fixed in their own parts by the caller.
 */
function applyShapeIssues(
  xml: string,
  issues: BrandIssue[],
  brand: BrandSpec,
  bodyFont: string,
  palette: string[],
): ShapeIssuesResult {
  let out = xml;
  const flags: TargetFlags = { diagramFonts: false, diagramFills: false, chartFonts: false, clearNotes: false };

  for (const issue of issues) {
    const target = issue.ooxmlTarget ?? "shape";
    if (target === "slide") { out = applySlideLevelIssue(out, issue); continue; }
    if (flagNonSlideTarget(issue, target, flags)) continue;
    out = applyShapeOrTableIssue(out, issue, target, brand, bodyFont, palette);
  }

  return { xml: out, ...flags };
}

/** SmartArt lives in separate diagram parts, unreachable via slide XML. Returns whether any part changed. */
function applyDiagramFixes(
  files: PptxZip,
  brand: BrandSpec,
  bodyFont: string,
  diagramFonts: boolean,
  diagramFills: boolean,
): boolean {
  if (!diagramFonts && !diagramFills) return false;
  let changed = false;
  const diagramPaths = Object.keys(files).filter((p) => /^ppt\/diagrams\/[^/]+\.xml$/.test(p));
  for (const path of diagramPaths) {
    let dxml = readZipText(files, path) ?? "";
    const dorig = dxml;
    if (diagramFills) dxml = recolourBlock(dxml, brand);
    if (diagramFonts && bodyFont) dxml = forceFontInRuns(dxml, bodyFont);
    if (dxml !== dorig) { files[path] = strToU8(dxml); changed = true; }
  }
  return changed;
}

/** Returns whether any chart part changed. */
function applyChartFixes(files: PptxZip, bodyFont: string, chartFonts: boolean): boolean {
  if (!chartFonts || !bodyFont) return false;
  let changed = false;
  const chartPaths = Object.keys(files).filter((p) => /^ppt\/charts\/chart\d+\.xml$/.test(p));
  for (const path of chartPaths) {
    const cxml = readZipText(files, path) ?? "";
    const rewritten = forceFontInRuns(cxml, bodyFont);
    if (rewritten !== cxml) { files[path] = strToU8(rewritten); changed = true; }
  }
  return changed;
}

/**
 * Clears the slide's speaker notes entirely — a deliberately destructive fix
 * (per explicit product decision), unlike every other OOXML target here.
 * Reuses SpeakerNotes' own read/write path (`writeNotesText`) rather than a
 * second hand-rolled notesSlide writer.
 */
function applyNotesFix(files: PptxZip, clearNotes: boolean): boolean {
  if (!clearNotes) return false;
  writeNotesText(files, "");
  return true;
}

/**
 * Fix a batch of OOXML-route issues that all live on the SAME slide with one
 * export → rewrite → reinsert cycle.
 */
async function fixIssuesViaOoxml(issues: BrandIssue[], brand: BrandSpec): Promise<boolean> {
  const first = issues[0];
  if (!first) return false;
  const bodyFont = resolveBodyFont(brand);
  const palette = buildFixPalette(brand);
  let applied = false;

  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();

    const slide = slides.items[first.slideIndex];
    if (!slide) return;

    const exportResult = slide.exportAsBase64();
    await context.sync();
    const slideId = slide.id;

    const files: PptxZip = await unzipPptxBase64(exportResult.value);
    const orig = readZipText(files, "ppt/slides/slide1.xml");
    if (orig === null) return;

    const { xml: withPlaceholders, bgBlocks } = preserveBackground(orig);
    const { xml: transformed, diagramFonts, diagramFills, chartFonts, clearNotes } =
      applyShapeIssues(withPlaceholders, issues, brand, bodyFont, palette);
    const xml = restoreBackground(transformed, bgBlocks);

    let changed = xml !== orig;
    if (changed) files["ppt/slides/slide1.xml"] = strToU8(xml);

    changed = applyDiagramFixes(files, brand, bodyFont, diagramFonts, diagramFills) || changed;
    changed = applyChartFixes(files, bodyFont, chartFonts) || changed;
    changed = applyNotesFix(files, clearNotes) || changed;

    if (!changed) return;

    // This is a same-deck round trip (export the slide we're already on, patch a
    // couple of attributes, reinsert) — NOT a foreign template merge like
    // apply-brand's use of this same helper. "UseDestinationTheme" reconciles the
    // incoming slide against the theme and was silently discarding explicit
    // overrides (bullet colour, forced font) that don't match the theme-derived
    // default, so the fix looked applied for a moment then reverted on rescan.
    // "KeepSourceFormatting" preserves the slide exactly as we exported + edited
    // it — the same mode SpeakerNotes/FormatTools use for their own self-fix
    // round trips.
    await replaceSlideWithRewrittenCopy(
      context,
      files,
      slideId,
      "KeepSourceFormatting" as PowerPoint.InsertSlideFormatting,
    );
    applied = true;
  });

  return applied;
}
